import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
// import { isEmail } from "../../utils";
import VendorForm from "./VendorForm";
import VendorView from "./VendorView";
import { Search, Plus } from "lucide-react";
import { vendorAPI } from "../../services";
import { toast } from "react-toastify";
import { handleFormattedValidationErrors } from "../../utils/errorHandlers";
import ArrowDownUpIcon from "../../assets/icons/ArrowDownUpIcon";

import "../../style/venders.css";

import EditIcon from "../../assets/icons/EditIcon";
import EyeIcon from "../../assets/icons/EyeIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import { VendorTableSkeleton } from "../skeleton";
import { useSelector } from "react-redux";

const Vendors = () => {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Ref to prevent double fetching in React StrictMode
  const initialFetchDone = useRef(false);

  // Load Suppliers from API
  const fetchVendors = async (search = "", page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorAPI.getVendorsList(
        search,
        page,
        itemsPerPage
      );
      // Handle the API response structure with results array
      const vendorsData = response.results || response.data || [];
      setVendors(vendorsData);
      setFilteredVendors(vendorsData);

      // Update pagination info from API response
      setTotalCount(response.count || 0);
      setHasNextPage(!!response.next);
      setHasPreviousPage(!!response.previous);
    } catch (error) {
      console.error("Error loading Suppliers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to load Suppliers. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      // Fallback to localStorage if API fails
      const storedVendors = localStorage.getItem("vendors");
      if (storedVendors) {
        try {
          const parsedVendors = JSON.parse(storedVendors);
          setVendors(parsedVendors);
          setFilteredVendors(parsedVendors);
          setTotalCount(parsedVendors.length);
          setHasNextPage(false);
          setHasPreviousPage(false);
        } catch (parseError) {
          console.error("Error parsing stored Suppliers:", parseError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Load vendors on component mount only (once, even with StrictMode)
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchVendors(searchTerm, currentPage);
    }
  }, []);

  // Handle search with debounce effect
  useEffect(() => {
    // Skip the initial render to avoid duplicate call
    if (!initialFetchDone.current || (searchTerm === "" && currentPage === 1)) {
      return;
    }

    const delayedSearch = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search
      fetchVendors(searchTerm, 1);
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Handle pagination (skip initial mount)
  useEffect(() => {
    if (initialFetchDone.current && currentPage > 1) {
      fetchVendors(searchTerm, currentPage);
    }
  }, [currentPage]);

  // Filter + sort (for client-side sorting)
  useEffect(() => {
    let filtered = [...vendors];

    if (sortField === "name" || sortField === "contact_email") {
      filtered.sort((a, b) => {
        let aValue = a[sortField] || "";
        let bValue = b[sortField] || "";
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    } else if (sortField === "") {
      // No sorting - keep original order (newest first by created_at)
      filtered.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // newest first
      });
    } else {
      // Default sorting by created_at
      filtered.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // newest first
      });
    }

    setFilteredVendors(filtered);
  }, [vendors, sortField, sortDirection]);

  // Apply sorting to search results when search term changes
  useEffect(() => {
    if (searchTerm) {
      // When searching, apply current sort to the search results
      let filtered = [...vendors];

      if (sortField === "name" || sortField === "contact_email") {
        filtered.sort((a, b) => {
          let aValue = a[sortField] || "";
          let bValue = b[sortField] || "";
          return sortDirection === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      setFilteredVendors(filtered);
    }
  }, [searchTerm, sortField, sortDirection, vendors]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> original (no sort)
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField("");
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleAddVendor = async (vendorData) => {
    try {
      setLoading(true);
      setError(null);

      // Call the create Supplier API
      await vendorAPI.createVendor(vendorData);

      // Refresh the Supplier list
      await fetchVendors(searchTerm, currentPage);
      setShowForm(false);
      toast.success("Supplier created successfully!");
    } catch (error) {
      console.error("Error creating Supplier:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create Supplier. Please try again.";
      setError(errorMessage);
      handleFormattedValidationErrors(error, errorMessage);

      // Fallback to localStorage only
      const newVendor = {
        id: Date.now().toString(),
        ...vendorData,
        created_at: new Date().toISOString(),
      };

      const updatedVendors = [...vendors, newVendor];
      setVendors(updatedVendors);
      localStorage.setItem("vendors", JSON.stringify(updatedVendors));
      setShowForm(false);
      toast.success("Supplier created successfully (offline mode)!");
    } finally {
      setLoading(false);
    }
  };
  const handleEditVendor = async (vendorData) => {
    try {
      setLoading(true);
      setError(null);

      // Call the update Supplier API
      await vendorAPI.updateVendor(editingVendor.id, vendorData);

      // Refresh the Supplier list
      await fetchVendors(searchTerm, currentPage);
      setShowForm(false);
      setEditingVendor(null);
      toast.success("Supplier updated successfully!");
    } catch (error) {
      console.error("Error updating Supplier:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update Supplier. Please try again.";
      setError(errorMessage);
      handleFormattedValidationErrors(error, errorMessage);

      // Fallback to localStorage only
      const updatedVendors = vendors.map((vendor) =>
        vendor.id === editingVendor.id
          ? { ...vendor, ...vendorData, updated_at: new Date().toISOString() }
          : vendor
      );

      setVendors(updatedVendors);
      localStorage.setItem("vendors", JSON.stringify(updatedVendors));
      setShowForm(false);
      setEditingVendor(null);
      toast.success("Supplier updated successfully (offline mode)!");
    } finally {
      setLoading(false);
    }
  };

  const openEditForm = (vendor) => {
    setEditingVendor(vendor);
    setShowForm(true);
  };

  const openViewModal = (vendor) => {
    navigate(`/vendor/${vendor.id}`);
  };

  // Use filtered Suppliers for client-side sorting, or Suppliers for server-side
  const currentVendors = filteredVendors;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="vendors-page-container">
      {/* Header - Fixed */}
      <div className="vendors-header">
        <div className="d-flex justify-content-between align-items-center gap-4 mb-4">
          <div>
            <h1 className="mrph-txt size-22 weight-600 color-text-medium">
              Suppliers
            </h1>
          </div>

          <div
            className="d-flex gap-4 justify-content-end align-items-center"
            style={{ width: "100%" }}
          >
            {/* Search Bar */}
            <div className="position-relative" style={{ width: "80%" }}>
              <input
                type="text"
                className="form-control text-search ps-5 mrph-form input-form mrph-bg"
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid #DCE3EB",
                  borderRadius: "6px",
                }}
                placeholder="Search customers by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <style jsx>{`
                .text-search::placeholder {
                  color: #8c959f !important;
                  font-family: Inter !important;
                  font-weight: 400 !important;
                  font-style: Regular !important;
                  font-size: 13px !important;
                  line-height: 100% !important;
                  letter-spacing: 0% !important;
                }
              `}</style>
              <Search
                size={16}
                className="position-absolute top-50 start-0 translate-middle-y ms-3 vendor-search-icon"
              />
            </div>

            {/* Add Supplier Button */}
            <button
              className={`${
                isPartsURL
                  ? "add-customer-btn-parts"
                  : isAccessoriesURL
                  ? "add-customer-btn-acc"
                  : "add-customer-btn"
              }`}
              onClick={() => setShowForm(true)}
              disabled={loading}
            >
              <Plus /> Add Supplier
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <div className="d-flex align-items-center">
              <span className="me-2">⚠️</span>
              {error}
              <button
                className="btn btn-sm btn-outline-danger ms-auto"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table Container - Scrollable */}
      <div className="vendors-table-container">
        <div className="vendor-table-responsive">
          {/* Fixed Header */}
          <div className="vendor-table-header-container">
            <table className="vendor-table">
              <thead className="vendor-table-header">
                <tr>
                  <th
                    className="vendor-th-name"
                    onClick={() => handleSort("name")}
                  >
                    <div className="vendor-th-content">
                      NAME
                      <ArrowDownUpIcon
                        color={sortField === "name" ? isPartsURL ? "#DB2E31" : isAccessoriesURL ? "#72BF48" : "#2F80ED" : "#57606A"}
                      />
                    </div>
                  </th>
                  <th>EMAIL</th>
                  <th>PHONE</th>
                  <th>ADDRESS</th>
                  <th style={{ textAlign: "end", paddingRight: "30px" }}>
                    ACTION
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Body */}
          <div className="vendor-table-body-container">
            <table className="vendor-table">
              <tbody className="vendor-table-body">
                {loading ? (
                  <VendorTableSkeleton rows={itemsPerPage} />
                ) : currentVendors.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-products-message">
                      <div
                        className=""
                        style={{
                          marginBottom: "4rem",
                          marginTop: "4rem",
                        }}
                      >
                        <NoDataIcon />
                        <h3 style={{ marginTop: "2rem" }}>
                          No Suppliers found
                        </h3>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentVendors.map((vendor, index) => (
                    <tr key={vendor.id}>
                      <td>{vendor.name}</td>
                      <td>{vendor.contact_email}</td>
                      <td>{vendor.phone_number}</td>
                      <td title={vendor.address}>{vendor.address}</td>
                      <td>
                        <div
                          className="action-buttons"
                          style={{ justifyContent: "flex-end" }}
                        >
                          <button
                            className={` ${isPartsURL ? "action-btn-parts view" : isAccessoriesURL ? "action-btn-acc view" : "action-btn view"}`}
                            onClick={() => openViewModal(vendor)}
                            title="View Supplier"
                          >
                            <EyeIcon fill={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)"} />
                          </button>
                          <button
                            className={`${isPartsURL ? "action-btn-parts view" : isAccessoriesURL ? "action-btn-acc view" : "action-btn view"}`}
                            onClick={() => openEditForm(vendor)}
                            title="Edit Supplier"
                          >
                            <EditIcon fill={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)"} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination - Fixed */}
      {totalCount > itemsPerPage && (
        <div className="vendors-pagination">
          <div className="d-flex justify-content-between align-items-center">
            {/* Left side: page info */}
            <div>
              <small className="text-search-placeholder">
                Page {currentPage} of {totalPages}
              </small>
            </div>

            {/* Right side: Previous & Next only */}
            <nav>
              <ul className="pagination pagination-sm mb-0 flex gap-3">
                <li
                  className={`page-item ${!hasPreviousPage ? "disabled" : ""}`}
                >
                  <button
                    className="vendor-pagination-btn"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!hasPreviousPage}
                  >
                    Previous
                  </button>
                </li>
                <li className={`page-item ${!hasNextPage ? "disabled" : ""}`}>
                  <button
                    className={`vendor-pagination-btn ${isPartsURL ? "vendor-pagination-btn-next-parts" : isAccessoriesURL ? "vendor-pagination-btn-next-acc" : "vendor-pagination-btn-next"}`}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!hasNextPage}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Supplier Form Modal */}
      {showForm && (
        <VendorForm
          vendor={editingVendor}
          onSave={editingVendor ? handleEditVendor : handleAddVendor}
          onCancel={() => {
            setShowForm(false);
            setEditingVendor(null);
          }}
        />
      )}

      {/* Supplier View Modal */}
      {showView && viewingVendor && (
        <VendorView
          vendor={viewingVendor}
          onClose={() => {
            setShowView(false);
            setViewingVendor(null);
          }}
        />
      )}
    </div>
  );
};

export default Vendors;
